// Keeps the address bar and the browser's history stack in step with where the
// app thinks it is.
//
// Two things the floor asked for fall out of this. A refresh comes back to the
// screen that was showing instead of dropping the operator at home — these
// tablets reload themselves for a service-worker update, and losing the pod you
// were on to that is infuriating. And the device's Back key goes home from
// anywhere, closing the app only from home, where there is nothing left to go
// back to.
//
// The stack is deliberately shallow: a home entry, then at most the current
// screen, then at most one open sheet. Screens replace one another rather than
// piling up, so Back is always one step from home however far someone wandered.

import { useEffect } from 'react';
import {
  historyPlan,
  homeRoute,
  parseRoute,
  routeDepth,
  routePath,
  screenOf,
  type Route,
} from '@/lib/route';
import { useStore } from '@/store';

/** What we write into `history.state` to recognise our own entries. */
interface Entry {
  sf: true;
  depth: number;
}

const entry = (depth: number): Entry => ({ sf: true, depth });

/** The depth of the entry showing, or null if this entry is not one of ours. */
function currentDepth(): number | null {
  const s = window.history.state as Partial<Entry> | null;
  return s && s.sf === true && typeof s.depth === 'number' ? s.depth : null;
}

/**
 * True between asking the browser to go back and the popstate that answers.
 *
 * `history.go` is asynchronous, and reconciling again in that window would
 * unwind twice for one navigation. The pending popstate is about to set the
 * state from the URL anyway, so there is nothing to lose by standing still.
 */
let unwinding = false;

/**
 * @param basePath The path of the screen UNDER the top entry — the same as
 * `path` except when the top entry is the andon sheet, whose URL names the
 * andon while the entry beneath it must name the tab. If both entries said
 * /alert/<id>, Back would pop to an entry that reopens the sheet it was
 * meant to close, and the sheet could never be left by the Back key.
 */
function reconcile(homePath: string, basePath: string, path: string, depth: number): void {
  // Stand still while an unwind is in flight: the popstate answering it is
  // about to set the app's state from the URL anyway.
  if (unwinding) return;

  const search = window.location.search;
  const url = path + search;
  let at = currentDepth();

  for (const step of historyPlan(at, depth)) {
    switch (step.kind) {
      case 'reset':
        // Lay a home entry underneath whatever was asked for, so Back has
        // somewhere to go rather than leaving the app the moment someone
        // refreshes on a job.
        window.history.replaceState(entry(0), '', homePath + search);
        at = 0;
        break;
      case 'push':
        at = (at ?? 0) + 1;
        window.history.pushState(entry(at), '', (at === depth ? path : basePath) + search);
        break;
      case 'replace':
        if (window.location.pathname !== path) window.history.replaceState(entry(depth), '', url);
        break;
      case 'back':
        unwinding = true;
        window.history.go(-step.steps);
        break;
    }
  }
}

/**
 * @param active Only once someone is signed in. Before that the login flow owns
 * the screen, and the URL is left alone so that whatever was asked for is still
 * there to restore when the session lands.
 */
export function useRouteSync(active: boolean): void {
  const isLeader = useStore((s) => s.isLeader);
  const leaderView = useStore((s) => s.leaderView);
  const tab = useStore((s) => s.tab);
  const screen = useStore((s) => s.screen);
  const openWoId = useStore((s) => s.openWoId);
  const openIswId = useStore((s) => s.openIswId);
  const overlay = useStore((s) => s.overlay);
  const openAndonId = useStore((s) => s.openAndonId);
  const goToRoute = useStore((s) => s.goToRoute);

  const base: Route = {
    leaderView: isLeader ? leaderView : 'station',
    tab,
    screen: screenOf(screen),
    woId: openWoId,
    iswId: openIswId,
    andonId: null,
  };
  // The andon sheet is the one overlay with an address — whichever sheet it
  // is: the thread, or the read-only one a session without the andon module
  // gets. A push can only open a URL, and a handover note can only carry
  // one, so the address has to mean the same andon on every device.
  const route: Route = {
    ...base,
    andonId: overlay === 'andonDetail' ? openAndonId : null,
  };
  const path = routePath(route);
  const basePath = routePath(base);
  const homePath = routePath(homeRoute(isLeader));
  // A sheet earns a history entry — Back should close it, not leave the screen
  // under it — but never a URL of its own (bar the andon sheet, see above).
  const depth = routeDepth(route, isLeader) + (overlay ? 1 : 0);

  useEffect(() => {
    if (!active) return;
    reconcile(homePath, basePath, path, depth);
  }, [active, homePath, basePath, path, depth]);

  useEffect(() => {
    if (!active) return;
    const onPop = () => {
      unwinding = false;
      goToRoute(parseRoute(window.location.pathname));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [active, goToRoute]);
}
